import { Stack, Typography, Box } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { fileNames, IFile, FileTypeError } from 'API';
import { FileDocument } from '../FileDocument';
import { Loader } from 'Components/Loader';
import { useEffect, useState } from 'react';

interface FilesSectionProps {
  files: fileNames;
  onFileUpdate: () => void;
  hasUnprocessedMandatoryFiles: boolean;
  hasUnprocessedOptionalFiles: boolean;
  pollingCount: number;
  checkedFiles: Record<number, boolean>;
  onCheckboxChange: (fileId: number, checked: boolean) => void;
}

export const mandatoryFilesCategoryId = [5];
export const POLLING_LIMIT = 8;
export const POLLING_INTERVAL = 7000;
export const POLLING_INTERVAL_FIRST_MESSAGE = 5000;
export const POLLING_INTERVAL_SECOND_MESSAGE = 10000;

export const FilesSection = ({
  files,
  onFileUpdate,
  hasUnprocessedMandatoryFiles,
  hasUnprocessedOptionalFiles,
  pollingCount,
  checkedFiles,
  onCheckboxChange,
}: FilesSectionProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.DOCUMENTS_HANDLE);
  const isDesktop = useIsDesktop();

  const [mandatoryLoaderMessage, setMandatoryLoaderMessage] = useState<
    string | null
  >(null);
  const [optionalLoaderMessage, setOptionalLoaderMessage] = useState<
    string | null
  >(null);

  const groupFilesByMandatory = (files: fileNames) => {
    const mandatory: IFile[] = [];
    const optional: IFile[] = [];

    Object.values(files).forEach((categoryFiles) => {
      categoryFiles?.forEach((file) => {
        if (
          file.analysisErrorCode === FileTypeError.ServiceUnavailable ||
          file.analysisErrorCode === FileTypeError.UnknownError
        ) {
          return;
        }

        if (pollingCount >= POLLING_LIMIT && file.isAnalysisOccured === false) {
          return;
        }

        if (file.isAnalysisOccured === false && pollingCount < POLLING_LIMIT) {
          return;
        }

        if (
          file.isAnalysisSucceeded === false &&
          file.isAnalysisOccured === true
        ) {
          if (mandatoryFilesCategoryId.includes(file.fileCategoryRelationId)) {
            mandatory.push(file);
          } else {
            optional.push(file);
          }
        }
      });
    });

    return { mandatory, optional };
  };

  const { mandatory, optional } = groupFilesByMandatory(files);

  useEffect(() => {
    setMandatoryLoaderMessage(null);
    setOptionalLoaderMessage(null);

    if (hasUnprocessedMandatoryFiles) {
      const firstMessageTimer = setTimeout(() => {
        setMandatoryLoaderMessage(dictionary.processingMayTakeTime);
      }, POLLING_INTERVAL_FIRST_MESSAGE);

      const secondMessageTimer = setTimeout(() => {
        setMandatoryLoaderMessage(dictionary.processingAlmostDone);
      }, POLLING_INTERVAL_SECOND_MESSAGE);

      return () => {
        clearTimeout(firstMessageTimer);
        clearTimeout(secondMessageTimer);
      };
    }
  }, [hasUnprocessedMandatoryFiles, dictionary]);

  useEffect(() => {
    setOptionalLoaderMessage(null);

    if (hasUnprocessedOptionalFiles) {
      const firstMessageTimer = setTimeout(() => {
        setOptionalLoaderMessage(dictionary.processingMayTakeTime);
      }, POLLING_INTERVAL_FIRST_MESSAGE);

      const secondMessageTimer = setTimeout(() => {
        setOptionalLoaderMessage(dictionary.processingAlmostDone);
      }, POLLING_INTERVAL_SECOND_MESSAGE);

      return () => {
        clearTimeout(firstMessageTimer);
        clearTimeout(secondMessageTimer);
      };
    }
  }, [hasUnprocessedOptionalFiles, dictionary]);

  return (
    <Stack gap={isDesktop ? '16px' : '12px'}>
      {(mandatory.length > 0 || hasUnprocessedMandatoryFiles) && (
        <Stack mb={5}>
          <Stack gap={isDesktop ? '8px' : '4px'} mb={2}>
            <Typography
              fontWeight='bold'
              fontSize={isDesktop ? '20px !important' : '18px !important'}
            >
              {dictionary.mandatoryFiles}
            </Typography>
            <Typography
              fontSize={isDesktop ? '18px !important' : '16px !important'}
            >
              {dictionary.mandatoryFilesNote}
            </Typography>
          </Stack>

          {hasUnprocessedMandatoryFiles && (
            <Box sx={{ position: 'relative', my: 2 }}>
              <Loader />
              {mandatoryLoaderMessage && (
                <Typography
                  align='center'
                  sx={{ mt: 2, color: 'text.secondary' }}
                >
                  {mandatoryLoaderMessage}
                </Typography>
              )}
            </Box>
          )}

          {mandatory.length > 0 &&
            !hasUnprocessedMandatoryFiles &&
            mandatory.map((file) => (
              <FileDocument
                key={file.id}
                file={file}
                onFileUpdate={onFileUpdate}
                isChecked={checkedFiles[file.id] || false}
                onCheckboxChange={onCheckboxChange}
              />
            ))}
        </Stack>
      )}

      {(optional.length > 0 || hasUnprocessedOptionalFiles) && (
        <Stack mb={5}>
          <Stack gap='8px'>
            <Typography
              fontWeight='bold'
              fontSize={isDesktop ? '20px !important' : '18px !important'}
            >
              {dictionary.optionalFiles}
            </Typography>
            <Typography
              fontSize={isDesktop ? '18px !important' : '16px !important'}
            >
              {dictionary.optionalFilesNote}
            </Typography>
          </Stack>

          {hasUnprocessedOptionalFiles && (
            <Box sx={{ position: 'relative', my: 2 }}>
              <Loader />
              {optionalLoaderMessage && (
                <Typography
                  align='center'
                  sx={{ mt: 2, color: 'text.secondary' }}
                >
                  {optionalLoaderMessage}
                </Typography>
              )}
            </Box>
          )}

          {optional.length > 0 &&
            !hasUnprocessedOptionalFiles &&
            optional.map((file) => (
              <FileDocument
                key={file.id}
                file={file}
                onFileUpdate={onFileUpdate}
                isChecked={checkedFiles[file.id] || false}
                onCheckboxChange={onCheckboxChange}
              />
            ))}
        </Stack>
      )}
    </Stack>
  );
};
